import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import Swal from 'sweetalert2';
import { changeStorePassword, storeLogout } from '../../services/storeAuthApi';

const initialForm = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const StoreChangePassword = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [saving, setSaving] = useState(false);

  const validate = () => {
    const nextErrors = {};

    if (!form.currentPassword.trim()) {
      nextErrors.currentPassword = 'Current Password is required.';
    }

    if (!form.newPassword.trim()) {
      nextErrors.newPassword = 'New Password is required.';
    } else if (form.newPassword.length < 8) {
      nextErrors.newPassword = 'New Password must contain at least 8 characters.';
    }

    if (!form.confirmPassword.trim()) {
      nextErrors.confirmPassword = 'Confirm New Password is required.';
    } else if (form.newPassword !== form.confirmPassword) {
      nextErrors.confirmPassword = 'Confirm New Password must match New Password.';
    }

    if (
      form.currentPassword &&
      form.newPassword &&
      form.currentPassword === form.newPassword
    ) {
      nextErrors.newPassword = 'New Password must be different from Current Password.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: '' }));
  };

  const togglePassword = (field) => {
    setShowPassword((current) => ({ ...current, [field]: !current[field] }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving || !validate()) return;

    setSaving(true);
    try {
      await changeStorePassword({
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      await Swal.fire({
        icon: 'success',
        title: 'Password Changed',
        text: 'Password changed successfully. Please login again using your new password.',
        confirmButtonText: 'OK',
      });

      try {
        await storeLogout();
      } catch (logoutError) {
        // Best-effort logout. Local credentials are still cleared below.
      }

      localStorage.removeItem('storeToken');
      localStorage.removeItem('storeUser');
      navigate('/store-login', { replace: true });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Unable to Change Password',
        text: error.message || 'Unable to change password. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const renderPasswordField = (field, label, autoComplete) => (
    <div className="store-password-field">
      <label htmlFor={field}>{label}</label>
      <div className={`store-password-input-wrapper ${errors[field] ? 'has-error' : ''}`}>
        <input
          id={field}
          name={field}
          type={showPassword[field] ? 'text' : 'password'}
          value={form[field]}
          onChange={handleChange}
          autoComplete={autoComplete}
          aria-invalid={Boolean(errors[field])}
          aria-describedby={errors[field] ? `${field}-error` : undefined}
        />
        <button
          type="button"
          className="store-password-toggle"
          onClick={() => togglePassword(field)}
          aria-label={showPassword[field] ? `Hide ${label}` : `Show ${label}`}
        >
          {showPassword[field] ? <EyeSlash /> : <Eye />}
        </button>
      </div>
      <span className="store-password-error" id={`${field}-error`}>
        {errors[field] || ' '}
      </span>
    </div>
  );

  return (
    <section className="store-change-password-page">
      <div className="store-change-password-card">
        <div className="store-change-password-header">
          <span>Store Login Security</span>
          <h2>Change Password</h2>
          <p>Update your Store Portal password. You will be asked to login again after a successful change.</p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {renderPasswordField('currentPassword', 'Current Password', 'current-password')}
          {renderPasswordField('newPassword', 'New Password', 'new-password')}
          {renderPasswordField('confirmPassword', 'Confirm New Password', 'new-password')}

          <div className="store-change-password-actions">
            <button type="submit" className="store-change-password-submit" disabled={saving}>
              {saving ? 'Changing Password...' : 'Change Password'}
            </button>
            <button
              type="button"
              className="store-change-password-cancel"
              onClick={() => navigate('/store/dashboard')}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default StoreChangePassword;
